/**
 * Platform analytics tracking service
 * Tracks page views, interactions, and user behavior for platform-level metrics
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Generate a session ID that persists for the browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get user info from localStorage
const getUserInfo = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      userId: user.userId || user.id || 'anonymous',
      email: user.email || ''
    };
  } catch {
    return { userId: 'anonymous', email: '' };
  }
};

// Track visit history for repeat visit detection
const markPageVisited = (pageKey) => {
  const visited = JSON.parse(sessionStorage.getItem('visited_pages') || '{}');
  const isRepeat = visited[pageKey] || false;
  visited[pageKey] = true;
  sessionStorage.setItem('visited_pages', JSON.stringify(visited));
  return isRepeat;
};

class PlatformAnalytics {
  constructor() {
    this.pageStartTime = null;
    this.lastActivityTime = null;
    this.activeTimeMs = 0;
    this.clickCount = 0;
    this.currentPage = null;
    this.activityCheckInterval = null;
  }

  /**
   * Start tracking a page visit
   * @param {string} eventType - e.g., 'page_view_resume_builder'
   * @param {string} pageUrl - Current page URL
   */
  startPageTracking(eventType, pageUrl) {
    this.currentPage = eventType;
    this.pageStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.activeTimeMs = 0;
    this.clickCount = 0;

    // Track user activity (mouse moves, clicks, keyboard)
    this.attachActivityListeners();

    // Start activity checking interval (every 5 seconds)
    this.activityCheckInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      // If user was active in last 5 seconds, count it as active time
      if (timeSinceActivity < 5000) {
        this.activeTimeMs += 5000;
      }
    }, 5000);

    // Track page unload
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
  }

  /**
   * Stop tracking and send analytics
   */
  async stopPageTracking() {
    if (!this.pageStartTime || !this.currentPage) return;

    const totalDurationMs = Date.now() - this.pageStartTime;
    const isRepeat = markPageVisited(this.currentPage);

    await this.trackEvent(this.currentPage, window.location.pathname, {
      duration_ms: totalDurationMs,
      active_duration_ms: this.activeTimeMs,
      click_count: this.clickCount,
      is_repeat_visit: isRepeat
    });

    this.cleanup();
  }

  /**
   * Track a specific event (click, interaction, etc.)
   */
  async trackEvent(eventType, pageUrl, metadata = {}) {
    const { userId, email } = getUserInfo();

    try {
      await fetch(`${API_BASE_URL}/analytics/platform/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_type: eventType,
          user_id: userId,
          user_email: email,
          session_id: getSessionId(),
          page_url: pageUrl,
          metadata
        })
      });
    } catch (error) {
      console.error('[PlatformAnalytics] Failed to track event:', error);
    }
  }

  /**
   * Attach activity listeners
   */
  attachActivityListeners() {
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
    };

    const trackClick = () => {
      this.clickCount++;
      updateActivity();
    };

    document.addEventListener('mousemove', updateActivity);
    document.addEventListener('keydown', updateActivity);
    document.addEventListener('click', trackClick);
    document.addEventListener('scroll', updateActivity);

    // Store listeners for cleanup
    this._listeners = { updateActivity, trackClick };
  }

  /**
   * Cleanup listeners and intervals
   */
  cleanup() {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }

    if (this._listeners) {
      document.removeEventListener('mousemove', this._listeners.updateActivity);
      document.removeEventListener('keydown', this._listeners.updateActivity);
      document.removeEventListener('click', this._listeners.trackClick);
      document.removeEventListener('scroll', this._listeners.updateActivity);
      this._listeners = null;
    }

    window.removeEventListener('beforeunload', this.handlePageUnload);
  }

  /**
   * Handle page unload
   */
  handlePageUnload() {
    if (this.pageStartTime && this.currentPage) {
      const totalDurationMs = Date.now() - this.pageStartTime;
      const isRepeat = markPageVisited(this.currentPage);
      const { userId, email } = getUserInfo();

      // Use sendBeacon for reliable unload tracking
      navigator.sendBeacon(
        `${API_BASE_URL}/analytics/platform/track`,
        JSON.stringify({
          event_type: this.currentPage,
          user_id: userId,
          user_email: email,
          session_id: getSessionId(),
          page_url: window.location.pathname,
          metadata: {
            duration_ms: totalDurationMs,
            active_duration_ms: this.activeTimeMs,
            click_count: this.clickCount,
            is_repeat_visit: isRepeat
          }
        })
      );
    }
  }
}

export default new PlatformAnalytics();
